import {
  Heart,
  GraduationCap,
  Gift,
  Cake,
  Clapperboard,
  Camera,
  Trophy,
  Briefcase,
  Plane,
  Map as MapIcon,
  Compass,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export type OccasionDef = {
  slug: string;
  label: string;
  Icon: LucideIcon;
  /** Short, friendly tip a host can act on to prepare the car. */
  hostTip: string;
  /**
   * Canonical taxonomy this granular occasion rolls up to for analytics.
   * Matches `event_types.slug` in the database. `null` means the occasion
   * deliberately doesn't have a clean canonical bucket (e.g. "Something
   * else") and should be excluded from event-type aggregations.
   *
   * Backend reference (id, slug, name):
   *   1  private_wedding              Wedding
   *   2  private_birthday             Birthday and Anniversary
   *   3  business_event               Company Event
   *   4  commercial_tv_advertisement  Photography and Advertising
   *   5  private_gala                 Prom and Graduation
   *   6  sightseeing                  Sightseeing
   *   7  confirmation                 Religious Ceremony
   *   8  commercial_exhibitions       Fairs and Exhibition
   *   9  private_bachelorparty        Stag and Hen Party (intentionally NOT
   *                                   exposed in any renter-facing picker)
   *  10  commercial_film              TV and Film Production
   *  11  business_vip                 VIP and Airport Transport
   *
   * The renter-facing picker stays granular ("Anniversary" vs "Birthday")
   * for a friendlier UX; this field is what the API uses to populate the
   * canonical `trips.event_type` column so analytics can group by the
   * standard taxonomy.
   */
  eventTypeSlug: string | null;
};

export const ALL_OCCASIONS: Record<string, OccasionDef> = {
  "wedding":          { slug: "wedding",          label: "Wedding",          Icon: Heart,          hostTip: "Polish chrome and trim, vacuum carpets, and have ribbon-friendly door handles ready.",        eventTypeSlug: "private_wedding" },
  "prom":             { slug: "prom",             label: "Prom",             Icon: GraduationCap,  hostTip: "Spotless interior and a polished exterior. Expect lots of photos.",                       eventTypeSlug: "private_gala" },
  // "Special event" replaces the granular Anniversary + Birthday chips on
  // both pickers; both old slugs are kept below for backward-compat with
  // bookings created before the merge so getOccasion() still resolves them.
  "special-event":    { slug: "special-event",    label: "Special event",    Icon: Gift,           hostTip: "Make it feel special: clean cabin, fresh interior, and a quick wash.",             eventTypeSlug: "private_birthday" },
  "anniversary":      { slug: "anniversary",      label: "Anniversary",      Icon: Gift,           hostTip: "Fresh interior, clean glass, and a small touch (e.g. flowers on the dash) goes a long way.",   eventTypeSlug: "private_birthday" },
  "birthday":         { slug: "birthday",         label: "Birthday",         Icon: Cake,           hostTip: "Make it feel special: clean cabin, full tank, and a quick wash.",                      eventTypeSlug: "private_birthday" },
  "film-tv":          { slug: "film-tv",          label: "Film / TV",        Icon: Clapperboard,   hostTip: "Touch up scuffs, remove personal items, and keep paperwork handy for the production.",         eventTypeSlug: "commercial_film" },
  "photo-shoot":      { slug: "photo-shoot",      label: "Photo shoot",      Icon: Camera,         hostTip: "Detail the body and wheels, clean glass, and remove visible plate frames if possible.",        eventTypeSlug: "commercial_tv_advertisement" },
  // "exhibition" is the slug we persist; the renter-facing label is "Brand
  // activation" — the standalone "brand-activation" chip below was retired
  // as a duplicate. Old bookings with occasion=exhibition will now display
  // as "Brand activation" via getOccasion().
  "exhibition":       { slug: "exhibition",       label: "Brand activation", Icon: Trophy,         hostTip: "Show-ready detail: paint, chrome, tyres dressed, and engine bay tidy.",                        eventTypeSlug: "commercial_exhibitions" },
  "corporate-event":  { slug: "corporate-event",  label: "Corporate event",  Icon: Briefcase,      hostTip: "Crisp interior, exterior wash, and arrive a few minutes early.",                              eventTypeSlug: "business_event" },
  "business-travel":  { slug: "business-travel",  label: "Business travel",  Icon: Plane,          hostTip: "Clear cabin, working chargers, and a full tank for an easy hand-off.",                         eventTypeSlug: "business_vip" },
  "leisure":          { slug: "leisure",          label: "Road trip",        Icon: MapIcon,        hostTip: "Full tank, tyres checked, and the cabin clean for a long drive.",                              eventTypeSlug: "sightseeing" },
  "airport-transfer": { slug: "airport-transfer", label: "Airport transfer", Icon: Plane,          hostTip: "Plenty of luggage room: clear the boot and back seats.",                               eventTypeSlug: "business_vip" },
  // "Sightseeing" replaces "Night out" on the chauffeur picker; the old
  // "night-out" slug is kept for backward-compat with prior bookings.
  "sightseeing":      { slug: "sightseeing",      label: "Sightseeing",      Icon: Compass,        hostTip: "Comfortable cabin, full tank, and a clean exterior for a relaxed day out.",                   eventTypeSlug: "sightseeing" },
  "night-out":        { slug: "night-out",        label: "Night out",        Icon: Sparkles,       hostTip: "Sparkling exterior and a fresh-smelling cabin for the evening.",                              eventTypeSlug: "sightseeing" },
  // Retired from active pickers — kept so existing bookings with
  // occasion=brand-activation still resolve to a clean label via
  // getOccasion(). New bookings should use "exhibition" instead.
  "brand-activation": { slug: "brand-activation", label: "Brand activation", Icon: Trophy,         hostTip: "Show-ready detail and remove anything that could conflict with branding.",                     eventTypeSlug: "commercial_tv_advertisement" },
  "other":            { slug: "other",            label: "Something else",   Icon: Sparkles,       hostTip: "A quick wash and clean interior is always appreciated.",                                       eventTypeSlug: null },
};

export function getOccasionsFor(rentalType: string | null | undefined): OccasionDef[] {
  const rt = (rentalType || "").toLowerCase();
  let slugs: string[];
  if (rt === "chauffeured" || rt === "chauffeur") {
    // Anniversary + Birthday merged into "special-event"; "night-out" replaced
    // by "sightseeing". Old slugs stay in ALL_OCCASIONS for back-compat.
    slugs = ["wedding", "prom", "special-event", "anniversary", "birthday", "corporate-event", "airport-transfer", "sightseeing", "other"];
  } else if (rt === "event") {
    // "brand-activation" removed as a duplicate of "exhibition" (which now
    // shows the label "Brand activation"); Birthday replaced by "special-event".
    slugs = ["wedding", "photo-shoot", "film-tv", "exhibition", "corporate-event", "special-event", "other"];
  } else {
    slugs = ["leisure", "anniversary", "birthday", "photo-shoot", "business-travel", "exhibition", "wedding", "other"];
  }
  return slugs.map((s) => ALL_OCCASIONS[s]).filter(Boolean);
}

export function getOccasion(slug: string | null | undefined): OccasionDef | null {
  if (!slug) return null;
  return ALL_OCCASIONS[slug] || null;
}
